'use client';

import { useCart } from 'hooks/useCart';
import { useRecommendation } from 'hooks/useRecommendation';
import { useAppSelector } from 'lib/hooks';
import { useState } from 'react';
import { selectReadyForRecommendation } from 'slices/selectors';
import { selectUser } from 'slices/userSlice';

const REC_TYPES = ['mapd', 'gap_pdp'] as const;

const DevRecommendationTrigger = () => {
  const isReady = useAppSelector(selectReadyForRecommendation);
  const user = useAppSelector(selectUser);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cartMessage, setCartMessage] = useState<string | null>(null);

  const recommendation = useRecommendation();
  const updateCart = useCart();

  const handleTriggerRecommendation = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    setCartMessage(null);

    try {
      if (!isReady) {
        setError(
          'Missing required information. Please ensure user has location, birthdate, and health needs filled in.'
        );
        setIsLoading(false);
        return;
      }

      await recommendation();

      const fromProfile =
        !!user.recommendation_type &&
        REC_TYPES.includes(
          user.recommendation_type as (typeof REC_TYPES)[number]
        );
      const cartType = fromProfile ? user.recommendation_type! : 'mapd';
      updateCart(cartType);
      setCartMessage(
        fromProfile
          ? `Using profile recommendation type: ${cartType}.`
          : 'Using mapd (set recommendation_type on the user to mapd or gap_pdp to choose).'
      );

      setSuccess(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Error fetching recommendations: ${errorMessage}`);
      // eslint-disable-next-line no-console
      console.error('Error fetching recommendations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section>
      <h3>Trigger Recommendation</h3>
      <p>Manually trigger plan recommendations for the current user.</p>
      {error && (
        <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>
      )}
      {success && cartMessage && (
        <div style={{ color: 'green', marginBottom: '1rem' }}>
          Recommendations fetched and cart updated. {cartMessage}
        </div>
      )}
      <button
        className="button primary"
        onClick={handleTriggerRecommendation}
        disabled={isLoading || !isReady}
      >
        {isLoading ? 'Loading...' : 'Trigger Recommendation'}
      </button>
      <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
        Cart uses your profile recommendation type when set to mapd or gap_pdp;
        otherwise it defaults to mapd.
      </p>
      {!isReady && (
        <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
          User must have location, birthdate, and health needs configured.
        </p>
      )}
    </section>
  );
};

export default DevRecommendationTrigger;
